import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Modal, Form, Button, Divider } from "semantic-ui-react";
import { ControlledDropdownV2, IStrictControlledDropdownV2Props } from "../../../../../components/ControlledForm";
import { useTimezone } from "../../../../../hooks/useTimezone";
import {
  Time,
  toCalendarDateTime,
  CalendarDate,
  toZoned,
  ZonedDateTime,
  parseDate,
  parseTime,
} from "@internationalized/date";
import useInspectors from "../../../hooks/useInspectors";
import React, { useEffect } from "react";
import usePermit from "../../../hooks/permits/usePermit";
import useInspection from "../../../hooks/inspections/useInspection";
import useInspectionRejectRequestMutation from "../../../hooks/inspections/useInspectionRejectRequestMutation";
import { InspectionStatuses } from "../citizen-inspection-list/constants/InspectionStatus";
import { ControlledPlainDatePicker } from "../InspectionDatePicker/ControlledInspectionDatePicker";
import BlockOutDayWarningMessage from "./components/BlockOutDayWarningMessage";
import OnSiteContactInfoFields from "./components/OnSiteContactInfoFields";
import InspectionRequestedDates from "./components/InspectionRequestedDates";
import InspectionRequestRejectionMessage from "./components/InspectionRequestRejectionMessage";
import ControlledInspectionTypeScheduleCalendar from "./components/ControlledInspectionTypeScheduleCalendar";
import useInspectionScheduledUpdate from "../../../hooks/inspections/useInspectionScheduledUpdate";
import ControlledInspectionTimePicker from "../InspectionTimeDropdown/ControlledInspectionTimePicker";

interface IScheduleInspectionModal {
  open: boolean;
  onClose: () => void;
  inspectionTypeId: any;
  inspectionId?: string | number;
}

export type OnSubmitHandler = ({
  inspectionDate,
  inspectionTime,
}: {
  inspectionDate: CalendarDate | null;
  inspectionTime: Time | "AM" | "PM" | null;
  assignee?: string | null;
  onSiteContactName?: string | null;
  onSiteContactPhone?: string | null;
  onSiteContactInstructions?: string | null;
}) => void;

export interface IScheduleInspectionModalForm {
  inspectionDate: CalendarDate | null;
  inspectionTime: Time | "AM" | "PM" | null;
  assignee: string | null;
  onSiteContactName: string;
  onSiteContactPhone: string;
  onSiteContactInstructions: string;
  onSiteContactApplicantInstructions: string;
  inspectionRequestRejectionMessage: string | null;
}

const ScheduleInspectionModal = ({ open, onClose, inspectionTypeId, inspectionId }: IScheduleInspectionModal) => {
  const { inspectionQuery } = useInspection({ id: inspectionId ?? "", enabled: !!inspectionId });
  const inspectorsQuery = useInspectors({ inspectionTypeId: inspectionTypeId });

  const permitId = (inspectionQuery.data?.data?.attributes as any)?.["record-id"] as number | null | undefined;
  const permitQuery = usePermit({ permitId: permitId ?? "" });
  const { inspectionRejectRequestMutation } = useInspectionRejectRequestMutation({
    id: inspectionId ?? "",
    onSuccess: () => {
      onClose();
    },
    onError: () => {
      onClose();
    },
  });

  const { scheduleInspection } = useInspectionScheduledUpdate({ id: Number(inspectionId) });

  const onSubmit: OnSubmitHandler = (data) => {
    const scheduledDate = data.inspectionDate;
    const scheduledTime = data.inspectionTime;

    let time: ZonedDateTime | null = null;
    if (scheduledDate && scheduledTime && scheduledTime !== "AM" && scheduledTime !== "PM") {
      time = toZoned(toCalendarDateTime(scheduledDate, scheduledTime), defaultTimezoneInfo.timezone);
    }

    let meridiem: "AM" | "PM" | null = null;
    if (scheduledTime === "AM" || scheduledTime === "PM") {
      meridiem = scheduledTime;
    }

    scheduleInspection({
      "assignee-id": data.assignee ? parseInt(data.assignee) : undefined,
      "scheduled-date": scheduledDate?.toString(),
      "scheduled-time": time?.toDate().toISOString(),
      "scheduled-meridiem": meridiem ?? undefined,
      "on-site-contact-name": data.onSiteContactName ?? undefined,
      "on-site-contact-phone": data.onSiteContactPhone ?? undefined,
      "on-site-contact-instructions": data.onSiteContactInstructions ?? undefined,
    });

    // TODO: Schedule the inspection here
    onClose();
  };

  const formContext = useForm<IScheduleInspectionModalForm>({
    defaultValues: {
      inspectionDate: null,
      inspectionTime: null,
      assignee: null,
      onSiteContactName: "",
      onSiteContactPhone: "",
      onSiteContactInstructions: "",
      onSiteContactApplicantInstructions: "",
    },
  });

  const { defaultTimezoneInfo } = useTimezone();

  useEffect(() => {
    if (permitQuery.data && inspectionQuery.data) {
      const inspectionAttributes = inspectionQuery.data?.data.attributes;
      const currentAssigneeId = (inspectionAttributes as any)["assignee-id"];

      let onSiteContactName = inspectionAttributes?.onSiteContactName;
      const onSiteContactPhone = inspectionAttributes?.onSiteContactPhone ?? "";
      const onSiteContactInstructions = inspectionAttributes?.onSiteContactInstructions ?? "";
      const onSiteContactApplicantInstructions = inspectionAttributes?.onSiteContactApplicantInstructions ?? "";

      if (!onSiteContactName) {
        const permitAttributes = permitQuery.data.data.attributes;
        const permitApplicant = permitAttributes["permits-contacts"].find((contact) => {
          return contact.is_applicant;
        });
        const applicantName = `${permitApplicant?.first_name ?? ""} ${permitApplicant?.last_name ?? ""}`.trim();
        onSiteContactName = applicantName;
      }

      const scheduledDate = inspectionAttributes["scheduled-date"];
      const scheduledTime = inspectionAttributes["scheduled-time"];
      const scheduledMeridiem = inspectionAttributes["scheduled-meridiem"];

      formContext.reset({
        inspectionDate: scheduledDate !== null ? parseDate(scheduledDate) : null,
        inspectionTime: scheduledTime !== null ? parseTime(scheduledTime) : scheduledMeridiem,
        assignee: currentAssigneeId,
        onSiteContactName: onSiteContactName,
        onSiteContactPhone: onSiteContactPhone,
        onSiteContactInstructions: onSiteContactInstructions,
        onSiteContactApplicantInstructions: onSiteContactApplicantInstructions,
      });
    }
  }, [inspectionQuery.data, permitQuery.data, inspectorsQuery.data]);

  const rejectRequestFormContext = useForm<{ inspectionRequestRejectionMessage: string | null }>({
    defaultValues: {
      inspectionRequestRejectionMessage: null,
    },
  });

  const loadingInspectors = inspectorsQuery.isLoading || inspectorsQuery.isFetching;

  const inspectors = inspectorsQuery.data?.data ?? [];
  const inspectorOptions: { key: number; value: number; text: string; className?: string }[] = inspectors.map(
    (employee) => {
      return {
        key: parseInt(employee.id),
        value: parseInt(employee.id),
        text: `${employee.attributes["first-name"]} ${employee.attributes["last-name"]}`,
      };
    },
  );

  const assigneeId = inspectionQuery.data?.data.attributes["assignee-id"];
  const inspectorOptionsHasCurrentInspector = !!inspectorOptions.find((option) => {
    return option.value === assigneeId;
  });

  if (!inspectorOptionsHasCurrentInspector && assigneeId && !loadingInspectors) {
    const assignee = inspectionQuery.data?.data.attributes.assignee;
    const currentAssigneeFirstName = assignee?.["data"]?.["attributes"]?.["first-name"] ?? "";
    const currentAssigneeLastName = assignee?.["data"]?.["attributes"]?.["last-name"] ?? "";
    inspectorOptions.push({
      key: Number(assigneeId),
      value: Number(assigneeId),
      text: `${currentAssigneeFirstName} ${currentAssigneeLastName}`,
      className: "tw-bg-[#fdf6f6] tw-text-[#81332f]",
    });
  }

  const status = inspectionQuery.data?.data.attributes.status;
  const isUnscheduled = status === InspectionStatuses.UNSCHEDULED;
  const request = inspectionQuery.data?.data.attributes.request;
  const hasRequest = request && request.preferences && request.preferences.length !== 0;

  const handleRejectInspectionRequest: SubmitHandler<{ inspectionRequestRejectionMessage: string | null }> = ({
    inspectionRequestRejectionMessage,
  }) => {
    if (!inspectionRequestRejectionMessage) {
      rejectRequestFormContext.setError("inspectionRequestRejectionMessage", {
        message: "Request message cannot be blank when rejecting",
      });
    } else {
      inspectionRejectRequestMutation(inspectionRequestRejectionMessage);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header>Schedule Inspection</Modal.Header>
      <Modal.Content>
        <FormProvider {...formContext}>
          <Form>
            {hasRequest && !isUnscheduled && (
              <>
                <InspectionRequestedDates id={inspectionId?.toString() ?? ""} />
                <Divider />
              </>
            )}
            <div className='tw-mb-8'>
              <ControlledInspectionTypeScheduleCalendar
                inspectionTypeId={inspectionTypeId}
                inspectionId={inspectionId?.toString()}
              />
            </div>
            <BlockOutDayWarningMessage inspectionTypeId={inspectionTypeId} />
            <Form.Group widths='equal' className='tw-mb-0'>
              <Form.Field required>
                <label htmlFor='assignee' className='tw-flex'>
                  {" "}
                  Inspector
                </label>
                <ControlledDropdownV2<IStrictControlledDropdownV2Props>
                  control={formContext.control}
                  name={"assignee"}
                  required
                  loading={loadingInspectors}
                  options={inspectorOptions}
                  rules={{
                    validate: {
                      _required: (value) => {
                        if (!value) {
                          return "Inspector cannot be blank";
                        }
                        return true;
                      },
                    },
                  }}
                />
              </Form.Field>
              <Form.Field>
                <Form.Group widths='equal'>
                  <Form.Field required error={!!formContext.errors.inspectionDate}>
                    <ControlledPlainDatePicker
                      control={formContext.control}
                      name={"inspectionDate"}
                      errors={formContext.errors}
                      rules={{
                        validate: {
                          _required: (value: any) => {
                            if (!value) {
                              return "Inspection date cannot be blank";
                            }
                            return true;
                          },
                        },
                      }}
                      datePickerProps={{
                        label: "Date",
                        required: true,
                      }}
                    />
                  </Form.Field>
                  <Form.Field>
                    <ControlledInspectionTimePicker
                      inspectionId={inspectionId}
                      name='inspectionTime'
                      control={formContext.control}
                    />
                  </Form.Field>
                </Form.Group>
              </Form.Field>
            </Form.Group>
            <OnSiteContactInfoFields id={inspectionId} control={formContext.control} errors={formContext.errors} />
          </Form>
        </FormProvider>
        <FormProvider {...rejectRequestFormContext}>
          <Form>
            <InspectionRequestRejectionMessage id={inspectionId} errors={rejectRequestFormContext.errors} />
          </Form>
        </FormProvider>
      </Modal.Content>
      <Modal.Actions>
        <Button secondary onClick={onClose}>
          Cancel
        </Button>
        {status === InspectionStatuses.REQUESTED && (
          <Button color='red' onClick={rejectRequestFormContext.handleSubmit(handleRejectInspectionRequest)}>
            Reject Scheduling Request
          </Button>
        )}
        <Button primary onClick={formContext.handleSubmit(onSubmit)}>
          Schedule Inspection
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ScheduleInspectionModal;
